﻿import {
    CheckboxChangeEvent,
    ColorPickerChangeEvent,
    NumericTextBoxChangeEvent,
    RadioGroupChangeEvent,
    SliderChangeEvent,
    SwitchChangeEvent,
    TextBoxChangeEvent
} from '@progress/kendo-react-inputs';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { UdViewModel } from '../../../../udView/udview';
import { DropdownList, DropdownListWithValueFieldChangeEvent } from '../../../components/inputs/DropdownListWithValueField';
import { patchFocusedWorkspaceView } from '../../../state/store';
import { RadioButtonList } from './forms';
import { useWorkspaceView } from './use-view';

type ViewSelector<T> = (view: UdViewModel) => T;

function createViewHook<T, E>(handler: (e: E) => T) {
    return (selector: ViewSelector<T>) => {
        const dispatch = useDispatch();
        const currentValue = useWorkspaceView(selector);
        const [value, setValue] = useState<T>(currentValue);

        useEffect(() => {
            setValue(currentValue);
        }, [currentValue]);

        const onChange = useCallback(
            (e: E) => {
                const newValue = handler(e);
                setValue(newValue);

                dispatch(
                    patchFocusedWorkspaceView((view: UdViewModel) => {
                        updateBySelector(view, selector, newValue);
                    })
                );
            },
            [selector, dispatch]
        );

        return { value, onChange };
    };
}

function createViewHookChecked<T, E>(handler: (e: E) => T) {
    return (selector: ViewSelector<T>) => {
        const dispatch = useDispatch();
        const currentValue = useWorkspaceView(selector);
        const [value, setValue] = useState<T>(currentValue);

        useEffect(() => {
            setValue(currentValue);
        }, [currentValue]);

        const onChange = useCallback(
            (e: E) => {
                const newValue = handler(e);
                setValue(newValue);

                dispatch(
                    patchFocusedWorkspaceView((view: UdViewModel) => {
                        updateBySelector(view, selector, newValue);
                    })
                );
            },
            [selector, dispatch]
        );

        return { checked: value, onChange };
    };
}

function updateBySelector<T>(view: UdViewModel, selector: ViewSelector<T>, newValue: T) {
    const path: string[] = [];
    const proxy = createRecordingProxy(path);

    try {
        selector(proxy as UdViewModel);
        let target: any = view;
        for (let i = 0; i < path.length - 1; i++) {
            target = target[path[i]];
        }
        target[path[path.length - 1]] = newValue;
    } catch {
        // Fallback: couldn't record path
    }
}

function createRecordingProxy(path: string[]): any {
    return new Proxy(
        {},
        {
            get(_, prop: string) {
                path.push(prop);
                return createRecordingProxy(path);
            }
        }
    );
}

export const useViewCheckboxValue = createViewHook<boolean, CheckboxChangeEvent>((e) => e.value);
export const useViewStringValue = createViewHook<string, TextBoxChangeEvent>((e) => e.value as string);
export const useViewSwitchValue = createViewHookChecked<boolean, SwitchChangeEvent>((e) => e.value);
export const useViewNumericValue = createViewHook<number | null, NumericTextBoxChangeEvent>((e) => e.value);
export const useViewSliderValue = createViewHook<number | undefined, SliderChangeEvent>((e) => e.value);
export const useViewColorValue = createViewHook<string, ColorPickerChangeEvent>((e) => e.value as string);

export const useViewRadioValue = <T>(data: RadioButtonList<T>, selector: ViewSelector<T>) => {
    const dispatch = useDispatch();
    const currentValue = useWorkspaceView(selector);
    const [value, setValue] = useState<T>(currentValue);

    useEffect(() => {
        setValue(currentValue);
    }, [currentValue]);

    const onChange = useCallback(
        (e: RadioGroupChangeEvent) => {
            const newValue = e.value;
            setValue(newValue);

            dispatch(
                patchFocusedWorkspaceView((view: UdViewModel) => {
                    updateBySelector(view, selector, newValue);
                })
            );
        },
        [selector, dispatch]
    );

    return {
        value,
        data,
        onChange
    };
};

export const useViewDropdownValue = <T>(data: DropdownList<T>, selector: ViewSelector<T>) => {
    const dispatch = useDispatch();
    const currentValue = useWorkspaceView(selector);
    const [value, setValue] = useState<T>(currentValue);

    useEffect(() => {
        setValue(currentValue);
    }, [currentValue]);

    const onChange = useCallback(
        (e: DropdownListWithValueFieldChangeEvent<T>) => {
            const newValue = e.value;
            setValue(newValue);

            dispatch(
                patchFocusedWorkspaceView((view: UdViewModel) => {
                    updateBySelector(view, selector, newValue);
                })
            );
        },
        [selector, dispatch]
    );

    return {
        value,
        data,
        onChange
    };
};
